import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, TextField, Button, Stack, Alert,
  FormControlLabel, Checkbox, MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';

import {
  getEstabelecimentoById,
  getEstabelecimentos,
  updateEstabelecimento,
  resolverCodigoMunicipio,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

// === mesmo visual do FormCliente ===
const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper'
  },
};
function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...zen.sectionCardSx, ...sx }}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

const onlyDigits = (v) => String(v || '').replace(/\D/g, '');
const fmtCNPJ = (v) => {
  const d = onlyDigits(v).slice(0,14);
  if (!d) return '';
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

export default function EstablishmentForm({
  embedded = false,
  estabelecimentoId = null,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const id = estabelecimentoId ?? params.id;  // edição por id ou fallback para o único estabelecimento
  const editing = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    id: 0,
    cnpj: '',
    razaoSocial: '',
    nomeFantasia: '',
    inscricaoEstadual: '',
    simplesNacional: false,
    tipoCalculoMargem: 0,
    codigoMunicipioIbge: '',
    email: '',
    telefone: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
  });

  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [ibgeStatus, setIbgeStatus] = useState('');
  const ibgeTimer = useRef(null);

  const titulo = useMemo(
    () => (editing ? `Editar Estabelecimento #${id}` : 'Editar Estabelecimento'),
    [editing, id]
  );

  const mapEstabelecimento = (data) => ({
    id: data.id,
    cnpj: data.cnpj || '',
    razaoSocial: data.razaoSocial || '',
    nomeFantasia: data.nomeFantasia || '',
    inscricaoEstadual: data.inscricaoEstadual || '',
    simplesNacional: Boolean(data.simplesNacional),
    tipoCalculoMargem: Number(data.tipoCalculoMargem) === 1 ? 1 : 0,
    codigoMunicipioIbge: data.codigoMunicipioIbge ?? '',
    email: data.email || '',
    telefone: data.telefone || '',
    logradouro: data.logradouro || '',
    numero: data.numero || '',
    complemento: data.complemento || '',
    bairro: data.bairro || '',
    cep: data.cep || '',
    cidade: data.cidade || '',
    estado: data.estado || '',
  });

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const { data } = await getEstabelecimentoById(id);
          setForm(mapEstabelecimento(data));
        } else {
          // quando a rota for /establishments/edit sem id, busca o único estabelecimento
          const { data } = await getEstabelecimentos();
          const e = Array.isArray(data) ? data[0] : data;
          if (!e) throw new Error('Estabelecimento não encontrado.');
          setForm(mapEstabelecimento(e));
        }
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Não foi possível carregar o estabelecimento.'));
      }
    })();
  }, [id]);

  useEffect(() => {
    const cidade = form.cidade?.trim();
    const uf = String(form.estado || '').trim();
    if (!cidade || uf.length !== 2) return undefined;

    if (ibgeTimer.current) clearTimeout(ibgeTimer.current);
    ibgeTimer.current = setTimeout(async () => {
      try {
        const { data } = await resolverCodigoMunicipio(cidade, uf);
        const codigo = data?.codigo;
        if (!codigo) {
          setIbgeStatus('Município não encontrado para esta cidade/UF.');
          return;
        }
        setForm((prev) => (
          String(prev.codigoMunicipioIbge) === String(codigo)
            ? prev
            : { ...prev, codigoMunicipioIbge: codigo }
        ));
        setIbgeStatus(`cMun ${codigo} — ${data.nome}/${data.uf}`);
      } catch {
        setIbgeStatus('Município não encontrado para esta cidade/UF.');
      }
    }, 400);

    return () => {
      if (ibgeTimer.current) clearTimeout(ibgeTimer.current);
    };
  }, [form.cidade, form.estado]);

  const onChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/establishments');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSaving(true);
    try {
      if (!form.razaoSocial?.trim()) throw new Error('Razão Social é obrigatória.');
      if (!form.nomeFantasia?.trim()) throw new Error('Nome Fantasia é obrigatória.');
      if (!form.logradouro?.trim() || !form.numero?.trim() || !form.bairro?.trim()) {
        throw new Error('Endereço incompleto (logradouro, número, bairro).');
      }
      if (!form.cidade?.trim() || !form.estado?.trim() || !form.cep?.trim()) {
        throw new Error('Cidade/Estado/CEP são obrigatórios.');
      }
      if (!form.inscricaoEstadual?.trim()) {
        throw new Error('Inscrição Estadual é obrigatória para emissão fiscal.');
      }
      const ibge = Number(onlyDigits(form.codigoMunicipioIbge));
      if (!ibge || String(ibge).length < 7) {
        throw new Error('Informe o código IBGE do município (7 dígitos).');
      }

      const payload = {
        id: form.id,
        cnpj: onlyDigits(form.cnpj),            // preserva o CNPJ vindo da API (campo travado)
        razaoSocial: form.razaoSocial,
        nomeFantasia: form.nomeFantasia,
        inscricaoEstadual: form.inscricaoEstadual.trim(),
        simplesNacional: Boolean(form.simplesNacional),
        tipoCalculoMargem: Number(form.tipoCalculoMargem) === 1 ? 1 : 0,
        codigoMunicipioIbge: ibge,
        email: form.email || null,
        telefone: onlyDigits(form.telefone) || null,
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento || null,
        bairro: form.bairro,
        cep: onlyDigits(form.cep),
        cidade: form.cidade,
        estado: String(form.estado || '').trim().toUpperCase(),
      };

      await updateEstabelecimento(form.id, payload);
      if (onSaved) onSaved('Estabelecimento atualizado');
      else navigate('/establishments');
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar o estabelecimento.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 1200, mx: 'auto' }} component="form" onSubmit={onSubmit}>
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          {titulo}
        </Typography>
      )}

      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      <Section title="Identificação" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="CNPJ"
              value={fmtCNPJ(form.cnpj)}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
              placeholder="00.000.000/0000-00"
              helperText="Campo somente leitura"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Razão Social"
              value={form.razaoSocial}
              onChange={onChange('razaoSocial')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Nome Fantasia"
              value={form.nomeFantasia}
              onChange={onChange('nomeFantasia')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Inscrição Estadual"
              value={form.inscricaoEstadual}
              onChange={onChange('inscricaoEstadual')}
              fullWidth
              required
              helperText="Obrigatória para NFC-e/NF-e"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Código IBGE do município"
              value={form.codigoMunicipioIbge}
              onChange={onChange('codigoMunicipioIbge')}
              fullWidth
              required
              inputProps={{ inputMode: 'numeric', maxLength: 7 }}
              helperText={ibgeStatus || 'Preenchido automaticamente pela cidade e UF'}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(form.simplesNacional)}
                  onChange={onChange('simplesNacional')}
                />
              }
              label="Simples Nacional (CRT 1)"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              select
              label="Cálculo da margem no cadastro de produto"
              value={String(form.tipoCalculoMargem ?? 0)}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                tipoCalculoMargem: Number(e.target.value) === 1 ? 1 : 0,
              }))}
              fullWidth
              helperText="Margem usa o preço de venda. Markup usa o custo. Vale para esta filial."
            >
              <MenuItem value="0">Margem (sobre o preço de venda)</MenuItem>
              <MenuItem value="1">Markup (sobre o custo)</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Section>

      <Section title="Endereço" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Logradouro" value={form.logradouro} onChange={onChange('logradouro')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Número" value={form.numero} onChange={onChange('numero')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Complemento" value={form.complemento} onChange={onChange('complemento')} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Bairro" value={form.bairro} onChange={onChange('bairro')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="CEP" value={form.cep} onChange={onChange('cep')} fullWidth placeholder="00000-000" required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Cidade" value={form.cidade} onChange={onChange('cidade')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField label="UF" value={form.estado} onChange={onChange('estado')} fullWidth inputProps={{ maxLength: 2 }} required />
          </Grid>
        </Grid>
      </Section>

      <Section title="Contato">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Email" type="email" value={form.email} onChange={onChange('email')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Telefone" value={form.telefone} onChange={onChange('telefone')} fullWidth placeholder="(00) 00000-0000" />
          </Grid>
        </Grid>
      </Section>

      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
        <Button
          id="btn-save"
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={<SaveIcon />}
          sx={{ minWidth: 160, background: zen.blue, '&:hover': { background: zen.blueDark }, textTransform: 'none', fontWeight: 700 }}
        >
          Salvar Alterações
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={fechar}
          startIcon={<CancelIcon />}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Cancelar
        </Button>
      </Stack>
    </Box>
  );
}
